// import { useWrappersFetcher } from "./connector"; // Unused for now

// Import generated types from spec.ts instead of defining manually
import type { components } from "./spec";

// Use generated types from spec.ts
export type GiftCheckoutData = components["schemas"]["GiftCheckoutDataSchema"];
export type CreateGiftPaymentIntentRequest =
  components["schemas"]["CreateGiftPaymentIntentRequest"];
export type CreateGiftPaymentIntentResponse =
  components["schemas"]["CreateGiftPaymentIntentResponse"];
export type VerifySessionResponse =
  components["schemas"]["VerifySessionResponse"];

const getCsrfToken = () => {
  const name = "csrftoken=";
  const cookies = decodeURIComponent(document.cookie).split(";");
  // eslint-disable-next-line no-restricted-syntax
  for (let cookie of cookies) {
    while (cookie.charAt(0) === " ") cookie = cookie.substring(1);
    if (cookie.indexOf(name) === 0) return cookie.substring(name.length);
  }
  return "";
};

// Stripe Gift Checkout Integration
export const createGiftPaymentIntent = async (
  request: CreateGiftPaymentIntentRequest,
): Promise<CreateGiftPaymentIntentResponse> => {
  try {
    // Creating gift payment intent

    // For now, since the API endpoint might not be generated yet, we'll simulate
    // In production, uncomment the real API call below

    // Mock response for testing
    // return {
    //   checkout_url: "https://checkout.stripe.com/c/pay/mock_session_url",
    //   payment_intent_id: "pi_mock_payment_intent",
    //   success: true,
    //   error_message: undefined,
    // };

    // Real API call (uncomment when backend is deployed and types are generated):

    // const fetcher = useWrappersFetcher();
    // const response = await fetcher.path("/api/v1/gift/create-payment-intent/").method("post").create()(request);
    // return response.data;

    // Use current domain for API calls to avoid CORS issues with ngrok
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    const apiUrl = `${baseUrl}/api/v1/gift/create-payment-intent/`;

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": getCsrfToken(),
      },
      credentials: "include",
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    return {
      checkout_url: "",
      payment_intent_id: "",
      success: false,
      error_message:
        error instanceof Error
          ? error.message
          : "API endpoint not available yet",
    };
  }
};

// Verify Stripe checkout session status
export const verifyGiftSession = async (
  sessionId: string,
): Promise<VerifySessionResponse> => {
  try {
    // Use current domain for API calls to avoid CORS issues with ngrok
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    const apiUrl = `${baseUrl}/api/v1/gift/verify-session/?session_id=${encodeURIComponent(sessionId)}`;

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": getCsrfToken(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    return {
      status: "error",
      payment_status: null,
      amount_total: null,
      currency: null,
      customer_email: null,
      success: false,
      error_message: "Unable to verify payment status",
    };
  }
};

// Helper function to create gift metadata for Stripe
export const createGiftStripeMetadata = (giftData: GiftCheckoutData) => ({
  purchase_type: "gift",
  sku: giftData.sku,
  payer_email: giftData.payer_email,
  payer_name: giftData.payer_name,
  payer_phone: giftData.payer_phone,
  receiver_email: giftData.receiver_email,
  receiver_phone: giftData.receiver_phone,
  receiver_location: giftData.receiver_location || "Unknown",
  receiver_age: giftData.receiver_age || "Unknown",
  relationship: giftData.relationship,
  personalized_message: giftData.personalized_message || "",
});

// React Hook for Gift Stripe Checkout
export const useGiftStripeCheckout = () => {
  const initiateGiftCheckout = async (
    giftData: GiftCheckoutData,
    successUrl = `${window.location.origin}/gift/success/?session_id={CHECKOUT_SESSION_ID}`,
    cancelUrl = `${window.location.origin}/gift/cancel/`,
  ) => {
    const response = await createGiftPaymentIntent({
      gift_data: giftData,
      success_url: successUrl,
      cancel_url: cancelUrl,
    });

    if (response.success && response.checkout_url) {
      // Check if we're in an iframe (like Storybook) - Stripe checkout can't run in iframes
      const inIframe = window.self !== window.top;

      // For demo purposes, handle mock URLs
      if (response.checkout_url.includes("mock_session_url")) {
        // In demo mode, just return success
        return true;
      }

      if (inIframe) {
        // In Storybook/iframe: just return success instead of redirecting
        return true;
      }

      // Redirect to real Stripe Checkout (outside iframe)
      window.location.href = response.checkout_url;
      return true;
    }

    // Failed to create payment intent
    throw new Error(
      response.error_message || "Failed to create payment intent",
    );
  };

  return {
    initiateGiftCheckout,
  };
};

// Utility function to convert personalization data to gift checkout data
export const convertPersonalizationToCheckoutData = (
  packageData: { sku: string; totalPrice: number },
  personalizationData: {
    payerFullName: string;
    payerEmail: string;
    payerPhone: string;
    recipientFullName: string;
    recipientEmail: string;
    recipientPhone: string;
    recipientLocation: string;
    recipientAge: string;
    relationship: string;
    personalizedMessage: string;
  },
): GiftCheckoutData => ({
  sku: packageData.sku,
  amount: Math.round(packageData.totalPrice * 100), // Convert to cents
  payer_email: personalizationData.payerEmail,
  payer_name: personalizationData.payerFullName,
  payer_phone: personalizationData.payerPhone,
  receiver_name: personalizationData.recipientFullName,
  receiver_email: personalizationData.recipientEmail,
  receiver_phone: personalizationData.recipientPhone,
  receiver_location: personalizationData.recipientLocation,
  receiver_age: personalizationData.recipientAge,
  relationship: personalizationData.relationship,
  personalized_message: personalizationData.personalizedMessage,
});
